import {
  CalendarDays,
  Check,
  Link as LinkIcon,
  Mail,
  RefreshCw,
  X as XIcon,
  AlertTriangle,
} from "lucide-react";

/**
 * Tarjeta de estado de la conexión Google. Muestra cuenta, fecha de conexión
 * y el estado de cada servicio (Calendar y Gmail). Si falta algún scope,
 * destaca el botón "Reconectar Google".
 */
export default function GoogleStatusCard({
  status,
  disabled,
  onConnect,
  onDisconnect,
  onRefresh,
}) {
  const needsReconnect = !!status.needs_reconnect;
  return (
    <div className="ds-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <CalendarDays size={28} className="text-[#6E8B3D] shrink-0 mt-0.5" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              Cuenta Google conectada
            </div>
            <div className="font-bold text-lg text-[#111110]">
              {status.connected ? status.email : "No conectado"}
            </div>
            {status.connected && (
              <div className="text-xs text-[#525252] mt-0.5">
                Conectada el {new Date(status.connected_at).toLocaleString("es-ES")}
              </div>
            )}
            <p className="text-xs text-[#525252] mt-2 max-w-xl">
              Una sola cuenta de Google se conecta al sistema. Desde ella se accede a Google
              Calendar (para sincronizar reparaciones) y a Gmail (para enviar emails desde la
              aplicación).
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {status.connected ? (
            <>
              <button
                onClick={onRefresh}
                data-testid="google-refresh"
                className="border border-[#E5E5E0] px-3 py-2 text-xs font-mono uppercase hover:bg-[#F7F7F4] inline-flex items-center gap-1"
              >
                <RefreshCw size={12} /> Recargar
              </button>
              {needsReconnect && (
                <button
                  onClick={onConnect}
                  disabled={disabled}
                  data-testid="google-reconnect"
                  className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 text-xs font-mono uppercase font-bold inline-flex items-center gap-1 disabled:opacity-40"
                >
                  <RefreshCw size={12} /> Reconectar Google
                </button>
              )}
              <button
                onClick={onDisconnect}
                disabled={disabled}
                data-testid="google-disconnect"
                className="border border-red-300 text-red-700 px-3 py-2 text-xs font-mono uppercase hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-40"
              >
                <XIcon size={12} /> Desconectar
              </button>
            </>
          ) : (
            <button
              onClick={onConnect}
              disabled={disabled}
              data-testid="google-connect"
              className="bg-[#6E8B3D] text-white px-4 py-2 text-sm font-semibold hover:bg-[#556C2E] inline-flex items-center gap-2 disabled:opacity-40"
            >
              <LinkIcon size={14} /> Conectar cuenta Google
            </button>
          )}
        </div>
      </div>

      {status.connected && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-[#E5E5E0]">
          <ServiceRow
            icon={<CalendarDays size={16} />}
            name="Google Calendar"
            connected={status.calendar_connected}
            scope="calendar"
          />
          <ServiceRow
            icon={<Mail size={16} />}
            name="Gmail"
            connected={status.gmail_connected}
            scope="gmail.send"
          />
        </div>
      )}

      {needsReconnect && status.connected && (
        <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900 inline-flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Necesitas <strong>reconectar Google</strong> para activar el envío de emails
            (faltan permisos de Gmail). Pulsa <em>Reconectar Google</em> y acepta el nuevo
            permiso.
          </span>
        </div>
      )}
    </div>
  );
}

function ServiceRow({ icon, name, connected, scope }) {
  return (
    <div className={`border ${connected ? "border-[#E5E5E0] bg-[#FAFAF8]" : "border-amber-200 bg-amber-50"} px-3 py-2.5 flex items-center justify-between gap-2`}>
      <div className="flex items-center gap-2 text-sm">
        <span className={connected ? "text-[#6E8B3D]" : "text-amber-700"}>{icon}</span>
        <span className="font-medium">{name}</span>
        <code className="text-[10px] font-mono text-[#525252]">{scope}</code>
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1 text-[#6E8B3D] text-xs font-bold">
          <Check size={14} /> Conectado
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-bold">
          <XIcon size={14} /> No conectado
        </span>
      )}
    </div>
  );
}
